import { MutableRefObject } from "react"
import { SearchResult } from "../../../types"
import { parseSearchQuery } from "../../utils/search-prefixes"
import If from "../if"
import SingleRowListSlim from "../skeletons/single-row-list-slim"
import SearchResultItem from "./result-item"
import RecentSearches from "./recent-searches"
import PrefixChips, { getPrefixLabel } from "./prefix-chips"

interface Props {
  query: string
  isLoadingResults: boolean
  searchResults: SearchResult[]
  recentSearches: SearchResult[]
  activeIndex: number
  itemRefs: MutableRefObject<(HTMLDivElement | null)[]>
  isAdmin: boolean
  // Taller rows and wider gutters for touch
  comfortable?: boolean
  // Show the `prefix:` shortcut chips above recent searches
  showPrefixChips?: boolean
  onClickItem: (result: SearchResult) => void
  onClickResetRecentSearches: () => void
  onPickPrefix?: (prefix: string) => void
  handleMasquerade: (loid: string) => void
  close: () => void
  onItemKeyDown: (e: React.KeyboardEvent, index: number) => void
}

// The list that lives inside both the desktop dropdown and the mobile sheet:
// results, loading/empty states, or recent searches when the query is short.
const SearchResultsBody = ({
  query,
  isLoadingResults,
  searchResults,
  recentSearches,
  activeIndex,
  itemRefs,
  isAdmin,
  comfortable = false,
  showPrefixChips = false,
  onClickItem,
  onClickResetRecentSearches,
  onPickPrefix,
  handleMasquerade,
  close,
  onItemKeyDown
}: Props) => {
  const { prefix, value } = parseSearchQuery(query)
  const isSearching = prefix ? value.length >= 1 : query.length >= 3
  const emptyStatePadding = comfortable ? "px-4 py-6" : "px-6 py-6"

  if (searchResults.length) {
    return (
      <>
        {searchResults.map((result, idx) => (
          <SearchResultItem
            key={`${result.id}-${result.item}-${idx}`}
            ref={el => (itemRefs.current[idx] = el)}
            isAdmin={isAdmin}
            isFirstItem={!idx || result.type !== searchResults[idx - 1].type}
            isActive={activeIndex === idx}
            comfortable={comfortable}
            data={result}
            onClickItem={onClickItem}
            handleMasquerade={handleMasquerade}
            closeModal={close}
            onKeyDown={e => onItemKeyDown(e, idx)}
          />
        ))}
      </>
    )
  }

  if (isSearching) {
    return (
      <If
        condition={isLoadingResults}
        then={<SingleRowListSlim rows={4} />}
        else={
          <div className={`${emptyStatePadding} text-sm text-neutral-400`}>
            No results for “{query}”. Try a name, email, NMLS or ID.
          </div>
        }
      />
    )
  }

  // A prefix with nothing after the colon yet: tell them what goes there
  // instead of showing recent searches or a false "no results".
  if (prefix) {
    return (
      <div className={`${emptyStatePadding} text-sm text-neutral-400`}>
        Type the {getPrefixLabel(prefix).toLowerCase()} to look it up.
      </div>
    )
  }

  return (
    <>
      {showPrefixChips && onPickPrefix && <PrefixChips onPick={onPickPrefix} />}
      <RecentSearches
        recentSearches={recentSearches}
        onClickItem={onClickItem}
        onClickResetRecentSearches={onClickResetRecentSearches}
        itemRefs={itemRefs}
        activeIndex={activeIndex}
        onItemKeyDown={onItemKeyDown}
        comfortable={comfortable}
      />
    </>
  )
}

export default SearchResultsBody
